import { Injectable, OnDestroy } from '@angular/core';
import { tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import {
    EventCentreHook,
    EventCentreHookService,
} from '../../notification/event-centre';
import {
    Category,
    CategoryAction,
    Item,
    ItemAction,
    WhileActionInProgressFn,
    WhileCategoryActionInProgressFn,
} from '../../notification/event-centre/core';
import { TaskItem } from '../core';
import { TaskListDialogService } from '../task-list';
import {
    IMPORTANCE_HIGH,
    IMPORTANCE_NORMAL,
    STATUS_NOT_STARTED,
} from '../task-list/dialog/transformer/view-task-item.transformer';

import { Command } from './command';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class TeamsCallEventCentreHookService
    implements OnDestroy, EventCentreHook
{
    private _taskListPromise: () => void;

    constructor(
        private readonly _hookService: EventCentreHookService,
        private readonly _taskListDialogService: TaskListDialogService,
    ) {
        this._bindTaskListPromise();

        this._hookService.register(this);
    }

    ngOnDestroy(): void {
        this._hookService.unregister(this);
    }

    ecDidOpen(
        atLogin: boolean,
        category: Category,
        whileActionInProgress: WhileActionInProgressFn,
    ): void {
        if (atLogin) {
            this._openTaskList(new URLSearchParams(), (modify, done) => {
                const catAction = category.actions?.find(
                    (e) => e.command?.startsWith('?command=teamscall:'),
                );
                whileActionInProgress(modify, catAction, done);
            });
        }
    }

    ecOnActionInvoked(item: Item, action: ItemAction): boolean {
        return false;
    }

    ecOnActionInvoked2(
        item: Item,
        action: ItemAction,
        category: Category,
        whileActionInProgress: WhileActionInProgressFn,
    ): boolean {
        if (
            !(
                action.command
                    ?.toLowerCase()
                    ?.startsWith('?command=teamscall:') ?? false
            )
        ) {
            return false;
        }

        return this._checkCommand(
            item,
            action.command,
            category,
            whileActionInProgress,
        );
    }

    ecOnItemsChanged(items: Item[]): void {}

    ecOnCategoryActionInvoked2(
        category: Category,
        action: CategoryAction,
        items: Item[],
        whileCategoryActionInProgress: WhileCategoryActionInProgressFn,
    ): void {
        if (
            !(
                action.command
                    ?.toLowerCase()
                    ?.startsWith('?command=teamscall:') ?? false
            )
        ) {
            return;
        }

        this._checkCategoryCommand(
            action.command,
            whileCategoryActionInProgress,
        );
    }

    ecWillClose(): void {
        // close task list if it was opened via event centre. _bindTaskListPromise() will cleanup _taskListPromise
        if (this._taskListPromise != null) {
            this._taskListDialogService.close();
        }
    }

    private _checkCommand(
        item: Item,
        command: string,
        category: Category,
        whileActionInProgress: WhileActionInProgressFn,
    ): boolean {
        const params = new URLSearchParams(command);
        const commandStr = params.get('command');

        switch (commandStr.toLowerCase()) {
            case Command.CREATE_TASK.toLowerCase():
                // yes, open task list, then create task
                this._openTaskList(params, (modify, done) => {
                    const catAction = category.actions?.find(
                        (e) => e.command?.startsWith('?command=teamscall:'),
                    );
                    whileActionInProgress(modify, catAction, done);
                });
                this._createTask(params);
                return true;
            default:
                return false;
        }
    }

    private _checkCategoryCommand(
        command: string,
        whileCategoryActionInProgress: WhileCategoryActionInProgressFn,
    ): void {
        const params = new URLSearchParams(command);
        const commandStr = params.get('command');

        switch (commandStr.toLowerCase()) {
            case Command.OPEN_TASK_LIST.toLowerCase():
                this._openTaskList(params, whileCategoryActionInProgress);
                break;
            default:
                break;
        }
    }

    private _openTaskList(
        params: URLSearchParams,
        whileCategoryActionInProgress: WhileCategoryActionInProgressFn,
    ): void {
        this._taskListDialogService.open();

        whileCategoryActionInProgress(
            'hidden',
            new Promise<void>((resolve) => {
                this._taskListPromise = resolve;
            }),
        );
    }

    private _createTask(params: URLSearchParams): void {
        const title = params.get('title');
        if (title == null || title.trim().length === 0) {
            return;
        }

        const importanceStr = params.get('importance');
        const dueDateTimeStr = params.get('dueDateTime');

        const importance =
            importanceStr?.toLowerCase() === IMPORTANCE_HIGH.toLowerCase()
                ? IMPORTANCE_HIGH
                : IMPORTANCE_NORMAL;
        const dueDateTime =
            dueDateTimeStr == null ? new Date() : new Date(dueDateTimeStr);

        const item: TaskItem = {
            msftTaskId: null,
            title,
            importance,
            status: STATUS_NOT_STARTED,
            createdDateTime: null,
            dueDateTime,
        };

        this._taskListDialogService.createTask(item);
    }

    private _bindTaskListPromise(): void {
        this._taskListDialogService.didClose
            .pipe(
                tap(() => {
                    this._taskListPromise?.();
                    this._taskListPromise = null;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
